/** English strings for integration settings (registry connections, TLD channels, VNNIC and eKYC providers). The Vietnamese file must have exactly the same keys. */
export const integrations = {
  common: {
    loading: 'Loading…',
    contextFailed: 'Could not load the integration settings of this deployment',
    refresh: 'Refresh',
    close: 'Close',
    cancel: 'Cancel',
    back: 'Back',
    saving: 'Saving…',
    all: 'All',
    yes: 'Yes',
    no: 'No',
    on: 'On',
    off: 'Off',
    newer: 'Newer',
    older: 'Older',
    more: 'Show more',
    outsideControlCenter: 'Set up outside the Control Center',
  },
  env: {
    short: { OTE: 'OT&E', PRODUCTION: 'Production' },
    long: { OTE: 'OT&E server (test)', PRODUCTION: 'Production server' },
    /** The same, inside a sentence ("… on the production server"). */
    inline: { OTE: 'OT&E (test) server', PRODUCTION: 'production server' },
    editing: 'You are changing the settings of this server: {{env}}',
    approvalRequired: 'Changes here are saved as a proposal. A second person must approve it and then someone applies it; nothing changes before that.',
    immediate: 'Changes here take effect immediately; running services pick them up within about {{seconds}} seconds.',
    oneConfiguration: 'Each server has one configuration. The other server (OT&E or production) keeps its own settings, set on that server.',
    deployment: 'This server:',
    refresh: 'Services pick up changes within about {{seconds}} seconds.',
    breakGlassTitle: 'Emergency mode: settings from the server',
    breakGlass: 'This deployment is set to ignore the VNNIC settings saved here and use the values configured on the server instead. Changes saved here are kept but take effect only when that mode is switched off.',
  },
  deployment: { ote: 'OT&E (test)', production: 'production' },
  /** "Fill in the OT&E / production address" helper of the address forms. */
  presetFill: {
    title: 'Fill in the OT&E / production address',
    use: { OTE: 'Fill in the OT&E address', PRODUCTION: 'Fill in the production address' },
    otherServer: 'For the other server: this server runs as {{env}}.',
  },
  channel: {
    DIRECT_EPP: 'Direct to the registry (EPP)',
    REGISTRY_REST: "Direct through the registry's web service (REST)",
    UPSTREAM_REGISTRAR: 'Through a partner registrar',
  },
  channelShort: { DIRECT_EPP: 'Direct (EPP)', REGISTRY_REST: 'Direct (web service)', UPSTREAM_REGISTRAR: 'Partner registrar' },
  channelHint: {
    DIRECT_EPP: 'Tino is accredited for the TLD and sends registrations, renewals and transfers to the registry itself over EPP, the standard registry protocol.',
    REGISTRY_REST: "Tino is accredited for the TLD, but the registry offers its own web service (REST) instead of EPP, so commands go through that service.",
    UPSTREAM_REGISTRAR:
      'Tino cannot register the TLD directly, so it registers through the web service of another accredited registrar. That registrar holds the domains and is named as their sponsoring registrar in VNNIC gTLD reports.',
  },
  channelGuide: { title: 'The three ways a TLD can be registered (channels)' },
  kind: { REGISTRY: 'Registry', UPSTREAM_REGISTRAR: 'Partner registrar' },
  kindHint: {
    REGISTRY: 'The organisation that runs the TLD. Tino sends registrations, renewals and transfers to it directly.',
    UPSTREAM_REGISTRAR:
      'Another accredited registrar. Tino registers domains through its service when a TLD cannot be registered directly; that registrar becomes the sponsoring registrar of those domains.',
  },
  protocol: { EPP: 'EPP', REST: 'Web service (REST)' },
  protocolHint: {
    EPP: 'The standard registry protocol, over an encrypted connection with a client certificate.',
    REST: 'The provider\'s own web service. For providers the platform already supports (such as WebNIC) registrations can be automated; for others the provider\'s technical documentation is needed first.',
  },
  adapter: { 'standard-epp': 'Standard EPP', 'vnnic-epp': 'VNNIC EPP Gateway', none: 'None yet', webnic: 'WebNIC' },
  adapterStatus: {
    AVAILABLE: 'Automatic registration available',
    NOT_AVAILABLE_NEEDS_SPECIFICATION: "Automatic registration not available yet: the provider's technical documentation is needed",
  },
  adapterNotice: {
    title: "Automatic registration not available yet: the provider's technical documentation is needed",
    body: "The platform keeps and checks this connection, and it can be chosen for a TLD, but it cannot send registrations, renewals or transfers through it until the provider's official technical documentation has been filed and support for it has been built. Contact the platform team to start that work.",
  },
  connectionStatus: { ACTIVE: 'Active', SUSPENDED: 'Suspended', RETIRED: 'Retired' },
  versionStatus: { PENDING_APPROVAL: 'Waiting for approval', ACTIVE: 'In force', SUPERSEDED: 'Replaced', REJECTED: 'Rejected', CANCELLED: 'Cancelled' },
  credentialStatus: { PENDING: 'Waiting for approval', ACTIVE: 'In use', ROTATED: 'Replaced', REVOKED: 'Revoked' },
  approvalStatus: {
    PENDING: 'Waiting for a second person',
    APPROVED: 'Approved, ready to apply',
    REJECTED: 'Rejected',
    EXPIRED: 'Expired',
    EXECUTED: 'Applied',
    CANCELLED: 'Cancelled',
  },
  closedReason: {
    APPROVAL_REJECTED: 'Closed: the approver rejected it',
    CANCELLED: 'Closed: cancelled before approval',
    REVOKED_BY_OPERATOR: 'Revoked by an operator',
  },
  permission: {
    'integration.manage': 'integration management',
    'registry.credential.manage': 'credential management',
  },
  authMethod: {
    HTTP_BASIC: 'Username and password',
    BEARER_TOKEN: 'Access token',
    API_KEY_HEADER: 'Access key sent with each request',
    OAUTH2_CLIENT_CREDENTIALS: 'Client ID and secret exchanged for a short-lived token',
    ADAPTER_DEFINED: "Defined by the provider's integration",
  },
  trafficClass: { INTERACTIVE: 'Customer actions', BACKGROUND: 'Background jobs', POLL: 'Registry messages', DROPCATCH: 'Drop catching' },
  trafficClassHint: {
    INTERACTIVE: 'Registrations, renewals and changes made by customers and staff.',
    BACKGROUND: 'Scheduled work such as renewals, checks and reconciliation.',
    POLL: 'Reading the message queue the registry keeps for Tino.',
    DROPCATCH: 'Registering expiring domains the moment they are released.',
  },
  tldClass: { VN: '.vn', GTLD: 'Generic (gTLD)', NEW_GTLD: 'New generic TLD', CCTLD: 'Country code', OTHER: 'Other' },
  source: { CONFIGURED: 'Set here', DEFAULT: 'default' },
  sourceHint: {
    CONFIGURED: 'Chosen in the Control Center.',
    DEFAULT: 'No choice made yet: the TLD uses its default connection.',
  },
  readiness: {
    ADAPTER_AVAILABLE: 'Automatic registration is available for this connection',
    ENVIRONMENT_ENABLED: 'The connection is switched on',
    ENDPOINT_CONFIGURED: 'The address is set',
    CREDENTIAL_ACTIVE: 'Sign-in credentials are in use',
    CERTIFICATE_ACTIVE: 'A client certificate is in use',
    CERTIFICATE_VALID_30_DAYS: 'The client certificate is valid for at least 30 more days',
    CAPABILITIES_IN_FORCE: "The registry's rules (capabilities) are recorded",
    SPONSORING_REGISTRAR_KNOWN: 'The sponsoring registrar for VNNIC gTLD reports is known',
  },
  capability: {
    supports_dnssec: 'DNSSEC',
    supports_idn: 'Internationalised names',
    supports_transfer: 'Transfers',
    supports_rgp: 'Redemption grace period',
    requires_documents: 'Requires registrant documents',
    max_registration_years: 'Maximum registration years',
    supports_epp: 'EPP',
    supports_contact_objects: 'Contact objects',
    supports_host_objects: 'Name server (host) objects',
    supports_fee_extension: 'Fees over EPP (fee extension)',
    supports_change_poll: 'Change notices by poll',
    supports_premium: 'Premium names',
    supports_restore: 'Restore',
    requires_local_presence: 'Local presence required',
    requires_restore_report: 'Restore needs a report',
    max_renew_years: 'Longest renewal (years)',
  },
  secret: {
    configured: 'Set',
    notConfigured: 'Not set',
    destroyed: 'Value destroyed',
    source: { STORED: 'Kept encrypted by the platform', REFERENCE: 'From the secret store' },
    reference: 'Reference',
    changed: 'Changed {{when}}',
    changedBy: 'Changed {{when}} by {{who}}',
    writeOnly: 'Never displayed: only whether it is set, where it is kept and who changed it.',
  },
  secretInput: {
    mode: { KEEP: 'Keep the current value', VALUE: 'Enter a new value', REFERENCE: 'Use the secret store' },
    keepHint: 'The value in use stays the same.',
    valueLabel: 'New value',
    valueHint: 'It is encrypted when saved and can never be displayed again, not even to you.',
    length_one: '({{count}} character)',
    length_other: '({{count}} characters)',
    fileLabel: 'Private key file',
    fileRead_one: 'Read {{name}} ({{count}} character). The content is not displayed.',
    fileRead_other: 'Read {{name}} ({{count}} characters). The content is not displayed.',
    referenceLabel: 'Secret store reference',
    referenceHint: 'Where the server finds the value: starts with file:, vault: or env:.',
    referenceHintProduction: 'Where the server finds the value: starts with file: or vault:. Production does not accept server environment variables (env:).',
    envForbidden: 'Production does not accept env: references. Use file: or vault:.',
    vaultMissing: 'This server has no vault configured; check that the services that use this setting can read it.',
    referenceFormat: 'A reference starts with file:, vault: or env: followed by the location.',
  },
  form: {
    reason: 'Reason',
    noteOptional: 'Note (optional)',
    reasonHint: 'Recorded in the change history together with your name.',
    optional: 'optional',
    range: '{{min}}–{{max}}',
    confirm: 'I have checked the values above.',
    confirmTest: 'I have checked the values above. The change takes effect immediately on the {{env}}.',
    confirmProduction: 'I have checked the values above and understand this changes PRODUCTION. It takes effect only after a second person approves it and it is applied.',
    saveNow: 'Save and apply',
    submitForApproval: 'Submit for approval',
  },
  outcome: {
    appliedTitle: 'Saved and in force',
    applied: 'Running services pick up the change within about {{seconds}} seconds.',
    pendingTitle: 'Saved as a proposal: waiting for approval',
    pending: 'Nothing has changed yet. A second person must approve the proposal; after that, someone applies it from the "Changes waiting for approval" panel.',
    approvalMeta: 'The approver needs the {{permission}} permission. The request expires after {{hours}} hours.',
    pendingEnv: 'Server: {{env}}.',
  },
  warnings: {
    title: 'Please check',
    ADAPTER_NOT_AVAILABLE: 'Automatic registration is not available through this connection yet (the provider’s technical documentation is needed).',
    CONNECTION_NOT_CONFIGURED: 'The chosen connection is not switched on or not ready yet.',
    NO_SPONSORING_REGISTRAR: 'No sponsoring registrar is recorded for this connection, so VNNIC gTLD reports will skip these domains.',
    DOMAINS_REMAIN_ON_PREVIOUS: 'Domains already registered stay with the connection that holds them.',
    CERTIFICATE_EXPIRES_SOON: 'The certificate expires in less than 30 days.',
    SESSION_BUDGET_HIGH: 'The number of sessions is above what VNNIC allows for one account.',
    PLAIN_HTTP: 'The address is not encrypted (http). This is accepted only outside production.',
    PRIVATE_ADDRESS: 'The address is on a private network.',
    VAULT_NOT_CONFIGURED_HERE: 'This server has no vault configured; the services that use the value must be able to read it.',
    SAME_ADDRESS_ALL_ENVIRONMENTS: 'VNNIC has published only one address for this service; the OT&E and production servers both use it.',
  },
  errors: {
    failed: 'The change could not be saved',
    meta: 'Code {{code}} · request {{requestId}}',
    code: {
      VALIDATION_FAILED: 'Some values need correcting:',
      REASON_REQUIRED: 'Please give a reason.',
      FORBIDDEN: 'You do not have the permission needed for this change.',
      MFA_REQUIRED: 'This needs a session signed in with two-factor authentication.',
      SELF_APPROVAL: 'You proposed this change, so another person must approve it.',
      TOTP_REQUIRED: 'Enter your current authenticator code to approve your own change.',
      TOTP_INVALID: 'The authenticator code is wrong or was already used. Wait for the next code and try again.',
      NOT_FOUND: 'It no longer exists. Refresh the page.',
      UNKNOWN_PROVIDER: 'Unknown service.',
      CONCURRENT_MODIFICATION: 'Someone changed this in the meantime. Close the dialog, refresh and try again.',
      PENDING_CHANGE_EXISTS: 'A change is already waiting for approval here. Approve, apply or cancel it first.',
      REQUEST_IN_PROGRESS: 'The same request is still being processed. Wait a moment and try again.',
      CODE_TAKEN: 'Another connection already uses this code.',
      CONNECTION_RETIRED: 'This connection is retired and cannot be used.',
      CONNECTION_IN_USE: 'TLDs are still routed to this connection. Move them to another connection first.',
      SAME_CONNECTION: 'This TLD already uses that connection.',
      NOTHING_TO_CHANGE: 'None of the selected TLDs needs changing.',
      NOT_CONFIGURED: 'There is no address to use yet.',
      ACTIVE_VERSION_NEEDS_CONFIRMATION: 'Confirm that you want to revoke the version in use.',
      ALREADY_REVOKED: 'It was already revoked.',
      SECRET_IN_USE: 'A version in use or waiting for approval still uses this value.',
      ALREADY_DESTROYED: 'The value was already destroyed.',
      SPONSOR_FROM_UPSTREAM: 'For a partner registrar, change its details on the connection page instead.',
      APPROVAL_NOT_APPROVED: 'The change has not been approved.',
      APPROVAL_EXPIRED: 'The approval expired. Propose the change again.',
      APPROVAL_ALREADY_EXECUTED: 'The change was already applied.',
      APPROVAL_STALE: 'The settings changed after this proposal, so it can no longer be applied. Cancel it and propose again.',
      APPROVAL_NOT_OPEN: 'The change is no longer open.',
      APPROVAL_NOT_PENDING: 'The change is no longer waiting for a decision.',
      IDEMPOTENCY_KEY_REUSED: 'This form was already sent with different values. Close the dialog and start again.',
      UNSUPPORTED_FOR_PROTOCOL: 'Not available for this kind of connection.',
      TEST_MODE_NOT_SUPPORTED: 'This check is not available for this kind of connection.',
      TEST_RATE_LIMITED: 'Checks are limited to protect the registry. Try again in {{seconds}} seconds.',
      EXERCISE_OTE_ONLY: 'The business test sends real commands and runs only on the OT&E (test) server.',
      EXERCISE_EPP_ONLY: 'The business test is available for EPP connections.',
      EXERCISE_IN_PROGRESS: 'A run is already in progress for this connection. Wait for it to finish.',
      EXERCISE_RATE_LIMITED: 'Runs are limited to protect the registry. Try again in {{seconds}} seconds.',
      NO_TEST_ZONE: 'No domain extension is served by this connection in OT&E, so there is no test name to use.',
      REGISTRIES_UNAVAILABLE: 'This server cannot reach registries.',
      LOGIN_NEEDS_ENDPOINT_IN_FORCE: 'A sign-in check uses the stored account, so it runs only against the address in force. Check a proposed address with “Connection, encryption and greeting” instead.',
      USE_INTEGRATION_ENDPOINTS: 'Integration changes are proposed from the integration settings pages, not from the general approvals page.',
      NOT_SUPPORTED: 'Not available for this connection.',
      AUTH_FAILED: 'WebNIC refused the sign-in.',
      UNAVAILABLE: 'WebNIC could not be reached. Try again later.',
      PKCS12_INVALID: 'This is not a readable .p12 / .pfx file, or it is protected in a way the platform does not support. Export it again from the tool that created it.',
      PKCS12_TOO_LARGE: 'The file is larger than 64 KB. A client certificate file is normally a few KB; check that you chose the right file.',
      PKCS12_BAD_PASSPHRASE: 'The password does not open this file. Type it again; it is the password chosen when the file was exported.',
      PKCS12_NO_KEY_OR_CERT: 'The file must contain the private key and the certificate issued for it. Export the certificate together with its private key (and its chain).',
      PKCS12_UNSUPPORTED_KEY: 'The key in this file is of a kind the platform does not accept. Use an RSA key or an ECDSA P-256 / P-384 key.',
      PKCS12_KEY_TOO_SMALL: 'The RSA key in this file is shorter than 2048 bits, which is no longer accepted. Ask for a new certificate with a stronger key.',
      CERTIFICATE_EXPIRED: 'The certificate in this file has expired. Ask the registry for a renewed certificate.',
      VNNIC_REGISTRAR_API_DISABLED: 'The VNNIC Registrar API is not set up on this server.',
      VNNIC_UNAUTHORIZED: "VNNIC refused Tino's sign-in (client id / secret, or this server's address is not on VNNIC's allowed list).",
      VNNIC_REJECTED: 'VNNIC refused the request.',
      GATEWAY_UNAVAILABLE: 'VNNIC did not answer. Please try again later.',
      CERTIFICATE_NOT_YET_VALID: 'The certificate in this file is not valid yet. Upload it on or after its start date, or check the server clock.',
    },
    field: {
      UNKNOWN_STEP: 'unknown step',
      NO_STEP_SELECTED: 'choose at least one step',
      REQUIRES_CHECK: 'needs the name check step',
      REQUIRES_CONTACTS: 'needs the contacts step',
      REQUIRES_CREATE: 'needs the registration step',
      REQUIRED: 'required',
      INVALID_CODE: 'use 2–32 lower-case letters, digits or hyphens',
      INVALID_HOST: 'not a valid host name or address',
      INVALID_PORT: 'must be between 1 and 65535',
      INVALID_URL: 'not a valid address',
      HTTPS_REQUIRED: 'production needs an encrypted (https) address',
      URL_CREDENTIALS_FORBIDDEN: 'must not contain a username or password',
      URL_QUERY_FORBIDDEN: 'must not contain parameters after “?” or “#”',
      OUT_OF_RANGE: 'outside the allowed range',
      TLS_REQUIRED_IN_PRODUCTION: 'production connections must be encrypted',
      INVALID_PEM: 'not a valid certificate text',
      KEY_PASSWORD_REQUIRED: 'this private key is password-protected; enter its password',
      KEY_PASSWORD_WRONG: 'the password does not open this private key',
      KEY_PASSWORD_ONLY_FOR_VALUE: 'a key password only applies to an uploaded or pasted key; a referenced key must be unencrypted',
      KEY_ENCRYPTION_UNSUPPORTED: 'this key encryption is not supported; convert the key to PKCS#8 (openssl pkcs8 -topk8 -v2 aes-256-cbc)',
      ADDRESS_NOT_ALLOWED: 'this address is not allowed',
      WRONG_PROTOCOL_SECTION: 'does not match the kind of connection',
      SECRET_REQUIRED: 'a value is needed',
      INVALID_SECRET_REFERENCE: 'not a valid secret store reference',
      DB_REFERENCE_NOT_ALLOWED: 'enter the value itself instead of this kind of reference',
      ENV_REFERENCE_FORBIDDEN_IN_PRODUCTION: 'production does not accept env: references',
      SECRET_TOO_LONG: 'the value is too long',
      EPP_PASSWORD_LENGTH: 'the password must have 6–16 characters',
      NOTHING_TO_KEEP: 'there is no current value to keep',
      CERTIFICATE_EXPIRED: 'the certificate has expired',
      CERTIFICATE_NOT_YET_VALID: 'the certificate is not valid yet',
      INVALID_PRIVATE_KEY: 'not a valid private key',
      KEY_CERTIFICATE_MISMATCH: 'the private key does not belong to this certificate',
      UPSTREAM_REQUIRES_REST: 'a partner registrar is connected through its web service',
      ADAPTER_REQUIRED: 'choose how to talk to it',
      UNKNOWN_ADAPTER: 'not available in this version of the platform',
      ADAPTER_KIND_MISMATCH: 'not available for this kind of connection',
      UPSTREAM_IDENTITY_REQUIRED: 'the partner registrar details are needed',
      IMMUTABLE: 'cannot be changed after creation',
      UNKNOWN_OPTION: 'unknown setting',
      INVALID_PATH: 'must start with “/”',
      TOO_MANY_TLDS: 'choose between 1 and 500 TLDs',
      DUPLICATE_TLD: 'a TLD is listed twice',
      UNSUPPORTED_AUTH_METHOD: 'this sign-in method is not supported for this provider',
      UPSTREAM_ONLY: 'only a partner registrar has these details',
      UNKNOWN_VERSION: 'that version belongs to other settings',
      RESERVED_SECRET_REFERENCE: 'this reference points to one of the platform’s own secrets or system files and cannot be used',
      INVALID_SITE_KEY: 'letters, digits, “_” and “-” only (at most 100)',
      TEST_KEY_NOT_ALLOWED: "Cloudflare's test keys cannot be used on the production server",
    },
  },
  fields: {
    enabled: 'Switched on',
    status: 'Status',
    adapter: 'Integration',
    name: 'Name',
    code: 'Code',
    kind: 'Kind',
    protocol: 'How it connects',
    connection: 'Connection',
    connection_id: 'Connection',
    channel: 'Channel',
    client_id: 'Login ID / client ID',
    secret: 'Password or key',
    private_key: 'Private key',
    certificate_pem: 'Certificate',
    certificate_not_after: 'Certificate valid until',
    certificate_subject: 'Certificate issued to',
    certificate_fingerprint_sha256: 'Certificate fingerprint (SHA-256)',
    base_version_id: 'Version to start from',
    base_url: 'Address',
    iana_id: 'IANA registrar ID',
    trade_name: 'Registrar name',
    legal_name: 'Legal name',
    api_spec_reference: 'Provider documentation',
    upstream: 'Partner registrar details',
    upstream_iana_id: 'IANA registrar ID',
    upstream_trade_name: 'Registrar name',
    upstream_legal_name: 'Legal name',
    tld_ids: 'TLDs',
    epp: 'EPP settings',
    rest: 'Web service settings',
    epp_host: 'Server address',
    epp_port: 'Port',
    epp_tls: 'Encrypted connection (TLS)',
    epp_server_name: 'Certificate name of the server',
    epp_ca_pem: 'Extra trusted certificate authorities',
    epp_connect_timeout_ms: 'Time to connect',
    epp_command_timeout_ms: 'Time to wait for a reply',
    epp_acquire_timeout_ms: 'Time to wait for a free session',
    epp_failure_threshold: 'Failures before pausing',
    epp_open_for_ms: 'Pause after repeated failures',
    epp_max_commands_per_session: 'Commands per session',
    epp_max_session_idle_ms: 'Close idle sessions after',
    epp_max_session_lifetime_ms: 'Renew sessions after',
    epp_classes: 'Session pools',
    class_sessions: 'Sessions',
    class_requests_per_second: 'Commands per second',
    class_burst: 'Short burst',
    rest_base_url: 'Service address',
    rest_auth_method: 'How Tino signs in',
    rest_auth_settings: 'Sign-in settings',
    rest_auth_settings_api_key_header: 'Name of the key field',
    rest_auth_settings_token_url: 'Address that issues tokens',
    rest_auth_settings_scope: 'Access scope',
    rest_account_id: 'Account / reseller ID at the provider',
    rest_connect_timeout_ms: 'Time to connect',
    rest_request_timeout_ms: 'Time to wait for a reply',
  },
  provider: {
    name: {
      VNNIC_REGISTRAR_API: 'VNNIC Registrar API',
      VNNIC_EKYC: 'VNNIC eKYC',
      VNNIC_HSTM: 'VNNIC dossier service (HSTM)',
      VNNIC_GTLD_REPORTING: 'VNNIC gTLD reporting',
      VNNIC_WHOIS: 'VNNIC WHOIS service',
      VNPT_SMARTCA: 'VNPT SmartCA (registrar signature)',
      CLOUDFLARE_TURNSTILE: 'Anti-robot check (Cloudflare Turnstile)',
    },
    tab: {
      VNNIC_REGISTRAR_API: 'Registrar API',
      VNNIC_EKYC: 'eKYC',
      VNNIC_HSTM: 'Dossiers (HSTM)',
      VNNIC_GTLD_REPORTING: 'gTLD reporting',
      VNNIC_WHOIS: 'WHOIS',
      VNPT_SMARTCA: 'SmartCA',
      CLOUDFLARE_TURNSTILE: 'Anti-robot check',
    },
    description: {
      VNNIC_REGISTRAR_API: 'Business registry lookups (enterprises and household businesses), free-domain promotion codes and household-business declarations with VNNIC.',
      VNNIC_EKYC: 'Reading identity documents and comparing the holder’s face when a subject’s identity is verified.',
      VNNIC_HSTM: 'Sending .vn domain dossiers (registrant declarations and documents) to VNNIC automatically.',
      VNNIC_GTLD_REPORTING: 'Reporting the international domains Tino manages to VNNIC: each change as it happens and the periodic list of maintained domains.',
      VNNIC_WHOIS: 'Availability and registration data of .vn names for “Tra cứu tên miền” and the customer API domain check, over mutual TLS with a WHOIS client certificate VNNIC issues separately from the EPP one.',
      VNPT_SMARTCA: 'The registrar signs the domain declaration and the files staff send for signing with VNPT SmartCA; confirmed in the SmartCA app.',
      CLOUDFLARE_TURNSTILE: 'Customers show they are not a robot (with Cloudflare Turnstile) when they sign in, sign up, forget their password, set a new password or accept an invitation to an account.',
    },
    offEffect: {
      VNNIC_REGISTRAR_API: 'When off, business registry lookups are not made and reviewers check organisations by hand.',
      VNNIC_EKYC: 'When off, identity checks wait (they stay pending) until the service is switched on again.',
      VNNIC_HSTM: 'When off, dossiers are exported for manual submission to VNNIC instead of being sent automatically.',
      VNNIC_GTLD_REPORTING: 'When off, no gTLD reports are sent to VNNIC.',
      VNNIC_WHOIS: 'When off, .vn lookups use our registry connection (EPP) or answer “unknown”.',
      VNPT_SMARTCA: 'When off, signing batches wait until SmartCA is on again.',
      CLOUDFLARE_TURNSTILE: 'When off, customers are not asked; an address that types wrong passwords too often is still locked out for a while.',
    },
    field: {
      base_url: 'Service address',
      timeout_ms: 'Time to wait for a reply',
      connect_timeout_ms: 'Time to connect',
      ocr_path: 'Document reading path',
      faceid_path: 'Face comparison path',
      max_attempts: 'Attempts per check',
      client_id: 'Client ID',
      maintain_hour_utc: 'Hour for the maintained-domain list',
      chunk_size: 'Domains per batch',
      sp_id: 'Service provider ID (SP ID)',
      user_id: "Signer's SmartCA account (ID card / tax code)",
      signature_text: 'Text shown on the signature',
      site_key: 'Site key',
      challenge_after_failures: 'When to ask',
    },
    fieldHint: {
      client_id: 'Issued by VNNIC; not secret.',
      sp_id: 'Issued by VNPT to the registrar.',
      signature_text: 'For example: NĐK Tino. The signing date is added on the next line.',
      site_key: "From Cloudflare's Turnstile page; not secret.",
    },
    secret: {
      client_id: 'Client ID',
      client_secret: 'Client secret',
      api_key: 'Access key',
      client_certificate: 'Client certificate (PEM, with its chain)',
      client_private_key: 'Client private key',
      client_key_passphrase: 'Key passphrase',
      ca_certificate: 'VNNIC CA certificate',
      sp_password: 'Service provider secret (SP secret)',
      secret_key: 'Secret key',
    },
    unit: { ms: 'ms', hour_utc: 'hour (UTC)', records: 'domains', attempts: 'attempts' },
    default: 'default',
  },
  whoisImport: {
    title: 'WHOIS client certificate',
    description: 'VNNIC issues a separate client certificate for the WHOIS service. Import the PKCS#12 (.p12 / .pfx) file: the certificate, the key (stored encrypted) and VNNIC’s CA go into a new version of these settings; the file’s passphrase is never stored.',
    open: 'Import PKCS#12…',
    dialogTitle: 'Import the WHOIS client certificate',
    file: 'PKCS#12 file (.p12 / .pfx)',
    passphrase: 'File passphrase',
    passphraseHint: 'Used once to open the file; never stored, logged or shown again.',
    ca: 'VNNIC CA certificate (PEM, optional)',
    caHint: 'Only when the file does not include VNNIC’s CA; otherwise the CA in the file is used.',
    baseUrl: 'Service address',
    baseUrlHint: 'Empty: the address VNNIC published for this kind of server (OT&E or production).',
    submit: 'Import',
    done: 'Certificate imported: {{subject}}, valid until {{until}}.',
    pending: 'Imported as a proposal: a second person must approve it before it is used.',
    tls: 'The connection uses TLS 1.2 with the AES256-SHA suite only (VNNIC’s server does not accept modern key exchange) and always verifies VNNIC’s certificate against this CA.',
  },
  applyMode: {
    allLive: 'Changes, including switching on or off, take effect within about {{seconds}} seconds.',
    enabled: {
      LIVE: 'Switching on or off takes effect within about {{seconds}} seconds.',
      RESTART: 'Switching on or off takes effect after the services restart.',
    },
    settings: {
      LIVE: 'Other changes take effect within about {{seconds}} seconds.',
      RESTART: 'Other changes take effect after the services restart.',
    },
  },
  applied: {
    none: 'No service has reported using these settings yet.',
    environment: 'Using server settings (environment variables)',
    disabled: 'Switched off',
    version: 'Using version {{version}}',
    behind: 'Not yet on version {{version}}',
    behindShort: 'Not on the version in force yet',
    checked: 'checked',
    formerSubject: 'former name',
    moduleSubject: 'as a module',
    subjectHint: 'Reported as {{subject}}',
    error: {
      RESTART_REQUIRED: 'Needs a restart to switch on or off',
      SECRET_UNAVAILABLE: 'Cannot read a password or key',
      CONFIGURATION_INVALID: 'Settings cannot be used; calls are stopped',
      SETTINGS_UNAVAILABLE: 'Could not read the saved settings; calls are stopped',
    },
  },
  service: {
    admin_api: 'Control Center server',
    api: 'Customer API',
    domain_worker: 'Domain processing',
    epp_poll_worker: 'Registry message reader',
    reconciliation_worker: 'Registry reconciliation',
    dropcatch_worker: 'Drop catching',
    scheduler: 'Scheduled jobs',
    identity_worker: 'Identity verification',
    vnnic_sync_worker: 'Dossier sending to VNNIC',
    vnnic_reconciliation_worker: 'Dossier reconciliation with VNNIC',
    vnnic_report_worker: 'gTLD reports to VNNIC',
    client_auth: 'Client area sign-in',
  },
  captcha: {
    howTitle: 'How it works',
    forms: "Used on the client area's sign-in, sign-up, forgotten password, new password and invitation pages.",
    when: '“Always ask”: every time those pages are sent. “After wrong attempts”: only once a network address or an e-mail address has typed a wrong password (or opened a wrong link) at least the chosen number of times in an hour; the sign-up and forgotten-password pages always ask because they send an e-mail.',
    failClosed: 'If it is on but cannot be checked (wrong secret key, Cloudflare not answering), those pages turn requests away until it is fixed. After every key change, use “Check reachability” and choose the sign-in check.',
    lock: 'An address that types wrong passwords too often is still locked out for a while, as before.',
    staff: 'The Control Center sign-in page does not use this check: it opens only from allowed networks and always needs a two-step verification code.',
    always: 'Always ask',
    afterChoice: 'After wrong attempts',
    afterFailures: 'After {{count}} wrong attempts',
    alwaysHint: 'Asks every time a page is sent.',
    afterHint: 'The sign-up and forgotten-password pages still always ask.',
    failures: 'Wrong attempts in an hour',
  },
  test: {
    tlsProblem: {
      UNKNOWN_ISSUER: "The server's certificate is issued by a private CA: add the registry's root CA (Certificates tab → Root CA).",
      NOT_VALID_FOR_NAME: "The server's certificate does not name the host connected to: check the address, or the TLS server name in the connection settings.",
      EXPIRED: "The server's certificate has expired.",
      NOT_YET_VALID: "The server's certificate is not valid yet (or the Control Center server's clock is off).",
      REVOKED: "The server's certificate has been revoked.",
      OTHER: "The server's certificate is not valid.",
    },
    serverChain: {
      title: 'Certificates the server presented',
      leaf: 'Server',
      noCa: "The server did not send its CA. Get the root CA file from the registry and upload it in the Certificates tab → Root CA.",
      compare: 'Before trusting it, compare the SHA-256 fingerprint with the one the registry publishes (or with the root CA file it sent).',
      trust: 'Use this CA as the root CA',
    },
    never: 'Never checked',
    none: 'No checks yet.',
    show: 'Details',
    hide: 'Hide',
    by: 'by',
    outcome: { OK: 'Passed', FAILED: 'Failed' },
    kind: {
      EPP_HANDSHAKE: 'Connection and greeting',
      EPP_LOGIN: 'Sign-in check with the stored account',
      HTTPS_REACHABILITY: 'Reachability check',
      API_LOGIN: 'Sign-in check',
    },
    step: {
      DNS: 'Address lookup',
      TCP: 'Connection',
      TLS: 'Encryption',
      GREETING: 'Registry greeting',
      LOGIN: 'Sign in',
      LOGOUT: 'Sign out',
      HTTP: 'Service answer',
      TOKEN: 'Get token',
      API_CALL: 'Authenticated API call',
    },
    stepStatus: { OK: 'OK', FAILED: 'Failed', SKIPPED: 'Skipped' },
    error: {
      DNS_NOT_FOUND: 'The address was not found.',
      ADDRESS_NOT_ALLOWED: 'Checks may not connect to this address.',
      CONNECT_REFUSED: 'The server refused the connection.',
      CONNECT_TIMEOUT: 'The server did not answer in time.',
      TLS_HANDSHAKE_FAILED: 'The encrypted connection could not be set up.',
      TLS_CERTIFICATE_INVALID: "The server's certificate is not trusted.",
      CLIENT_CERTIFICATE_REJECTED: "The server rejected Tino's client certificate.",
      GREETING_TIMEOUT: 'The registry did not send its greeting in time.',
      GREETING_INVALID: 'The greeting could not be read.',
      LOGIN_REJECTED: 'The registry rejected the sign-in.',
      LOGIN_TIMEOUT: 'The registry did not answer the sign-in in time.',
      HTTP_TIMEOUT: 'The service did not answer in time.',
      HTTP_INVALID_RESPONSE: 'Something answered at this address, but not a web service.',
      SECRET_UNAVAILABLE: 'A stored password or key could not be read.',
      CONFIGURATION_INVALID: 'The settings cannot be used as they are.',
      WRONG_CREDENTIALS: 'Wrong API username or password.',
      IP_NOT_ALLOWED: "The provider does not allow this server's IP address yet: add it to the account's allowed IP list.",
      CREDENTIALS_REJECTED: "The provider refused the sign-in without saying why: check the API username, password and the account's allowed IP list.",
      API_REJECTED: 'Signed in, but the provider refused the read.',
      API_TIMEOUT: 'The provider did not answer in time.',
      API_UNREACHABLE: 'The provider could not be reached (address lookup, connection or encryption failed).',
      API_UNEXPECTED_RESPONSE: 'The provider answered unexpectedly.',
    },
    interpretation: {
      REACHABLE: 'The service answered.',
      REACHABLE_AUTH_REQUIRED: 'The service answered and asks for sign-in (expected: no credentials are sent by this check).',
      REACHABLE_REDIRECT: 'The service answered with a redirect (not followed).',
      REACHABLE_SERVER_ERROR: 'The service answered with a server error.',
    },
    viaProxy: 'Looked up by the outgoing proxy',
    serverCertificate: 'Server certificate {{subject}}, valid {{days}} more days',
    clientCertificate: 'Client certificate version {{version}}',
    serverId: 'Server: {{id}}',
    services: '{{objects}} object types, {{extensions}} extensions offered',
    missingExtensions: 'Not offered by the server: {{list}}',
    resultCode: 'Registry answer {{code}}: {{message}}',
    httpStatus: 'Status {{status}}',
    endpoint: 'Call: {{endpoint}}',
    providerCode: 'Answer code: {{code}}',
    balances: 'Balance: {{list}}',
    itemCount: '{{count}} entries received',
    egress: "This server's outgoing IP address: {{list}}",
    loginNote: 'This check uses the stored credentials; the password and token are never shown or recorded.',
    testedVersion: 'Settings version {{version}}',
    credentialVersion: 'Credentials version {{version}}',
    certificateVersion: 'Certificate version {{version}}',
    probe: 'Checked from: {{service}}. The services that use this connection may connect from another network address, and VNNIC and most registries only accept registered addresses.',
    bannerNote: 'A check changes nothing; it is recorded in the history.',
    reachabilityOnly: 'This check sends no credentials, personal data or business request; it does not prove that the stored credentials work.',
    dialogTitle: 'Check the connection to {{name}}',
    dialogDescription: 'Checks run from the Control Center server and are recorded in the change history.',
    providerDialogTitle: 'Check the connection to {{name}}',
    modeLabel: 'What to check',
    mode: {
      HANDSHAKE: 'Connection, encryption and greeting',
      LOGIN: 'Sign-in check with the stored account',
      REACHABILITY: 'Reachability check',
      API_LOGIN: 'Sign-in check',
    },
    modeHint: {
      API_LOGIN: 'Signs in with the stored credentials, then makes one read ({{call}}). Nothing is changed at the provider and nothing is charged.',
      HANDSHAKE: "Connects to the registry, sets up encryption with the client certificate and reads the registry's greeting. Does not sign in.",
      LOGIN:
        'Also signs in with the stored account and signs out again. This opens one real registry session, which counts toward the registry’s session limit. Allowed once every {{seconds}} seconds and {{perHour}} times an hour.',
      REACHABILITY: 'Looks up the address, sets up encryption and asks the service for one answer, without credentials or data. It does not check the username, password or key.',
    },
    versionLabel: 'Which settings',
    activeVersion: 'Settings in force (version {{version}})',
    pendingVersion: 'Proposed settings (version {{version}}, waiting for approval)',
    credentialLabel: 'Which credentials',
    credentialActive: 'Credentials in use',
    credentialPending: 'Proposed credentials (version {{version}})',
    limits: 'One check every {{seconds}} seconds per connection.',
    address: 'Address',
    noAddress: 'No address is set yet.',
    run: 'Run check',
    runAgain: 'Run again',
    running: 'Checking…',
    loginOnlyInForce: 'Only for the version in force: a proposed address is checked without signing in.',
    apiLoginOnlyInForce: 'Only for the address in force: the stored credentials are never sent to an address waiting for approval.',
    loginCall: {
      webnic: 'the account balance',
      VNNIC_REGISTRAR_API: 'the deposit balance',
      VNNIC_HSTM: 'the list of dossier types',
      other: 'a read',
    },
  },
  changes: {
    selfApprove: 'Approve myself',
    selfApproveTitle: 'Approve your own change',
    selfApproveDescription: 'For administrators when no second person is available. Recorded in the history as a self-approval.',
    selfApproveNoticeTitle: 'You are approving your own change',
    selfApproveNotice: 'No second person checks this change. Read every value above carefully; the decision is recorded with your name and marked as a self-approval.',
    totp: 'Current authenticator code',
    totpHint: 'The 6-digit code from your authenticator app (not the one used to sign in).',
    mineSelfApprove: 'You proposed this change. As an administrator you may approve it yourself (re-entering your authenticator code); if a second person is available, let them approve it.',
    selfApproved: 'Approved by the person who proposed it (administrator self-approval).',
    title_one: 'Change waiting for approval ({{count}})',
    title_other: 'Changes waiting for approval ({{count}})',
    description: 'Production changes are saved as proposals. A second person approves them; then someone with the same permission applies them.',
    none: 'No changes are waiting for approval.',
    noneHere: 'Nothing is waiting on this page.',
    loading: 'Loading changes waiting for approval…',
    loadFailed: 'Could not load the changes waiting for approval',
    elsewhere_one: '{{count}} change is waiting on another page:',
    elsewhere_other: '{{count}} changes are waiting on other pages:',
    page: { registries: 'Registry connections', tlds: 'TLD channels', vnnic: 'VNNIC integrations', modules: 'Modules' },
    kind: {
      CONNECTION_UPDATE: 'Connection details',
      ENVIRONMENT_VERSION: 'Address and connection settings',
      CREDENTIAL_VERSION: 'Sign-in credentials',
      CERTIFICATE_VERSION: 'Client certificate',
      TLD_CHANNEL: 'TLD channel',
      TLD_CHANNEL_BATCH: 'Channel for several TLDs',
      PROVIDER_SETTING_VERSION: 'VNNIC service settings',
      SPONSORING_REGISTRAR: 'Sponsoring registrar (gTLD reports)',
      MODULE_INSTANCE_VERSION: 'Module settings',
    },
    version: 'version {{version}}',
    tldCount_one: '{{count}} TLD',
    tldCount_other: '{{count}} TLDs',
    requestedBy: 'Proposed by',
    decidedBy: 'Decided by',
    expires: 'expires {{when}}',
    reason: 'Reason',
    stale: 'The settings changed after this proposal, so it can no longer be applied. Cancel it and propose again.',
    mine: 'You proposed this change. Another person with the {{permission}} permission must approve it.',
    waiting: 'Waiting for a person with the {{permission}} permission.',
    approvedWaiting: 'Approved. A person with the {{permission}} permission can apply it.',
    noFieldChanges: 'No field changes.',
    hiddenValue: 'changed (hidden: needs the credential management permission)',
    tldMore_one: 'and {{count}} more',
    tldMore_other: 'and {{count}} more',
    secretReplaced: 'replaced ({{source}})',
    secretRemoved: 'removed',
    decide: 'Approve or reject',
    apply: 'Apply change',
    cancel: 'Cancel change',
    decideTitle: 'Approve or reject this change',
    decideDescription: 'You are the second person. Check the values against the request before deciding.',
    decision: 'Decision',
    approve: 'Approve',
    reject: 'Reject',
    approveAndApply: 'Approve and apply',
    applyNow: 'Apply right after approving (two separate steps, both recorded)',
    confirmApprove: 'I have checked this change and approve it in my own name.',
    confirmReject: 'I reject this change; the proposal is closed.',
    applyTitle: 'Apply the approved change',
    applyDescription: 'Exactly the approved proposal is applied; nothing can be edited at this point.',
    confirmApply: 'Apply the approved change now.',
    cancelTitle: 'Cancel this change',
    cancelDescription: 'The proposal is closed and nothing changes.',
    cancelConfirm: 'Cancel change',
    finished: { approved: 'Approved', applied: 'Applied', rejected: 'Rejected' },
    bannerDecide: 'Approving does not change anything by itself; the change takes effect when it is applied.',
    bannerApply: 'Applying puts the approved change in force; running services pick it up within about {{seconds}} seconds.',
    finishedApprovedHint: 'The change can now be applied from the panel.',
  },
  connections: {
    title: 'Registry connections',
    subtitle: 'How Tino reaches each registry: directly (EPP or the registry’s web service) or through a partner registrar.',
    add: 'Add connection',
    explainTitle: 'Connections and channels',
    explain:
      'A connection is how Tino sends registrations, renewals and transfers: directly to the registry, or through a partner registrar that registers the domain on Tino’s behalf. Each TLD then uses one connection; that choice is its channel.',
    toTlds: 'Choose the channel of each TLD →',
    filter: { kind: 'Kind', protocol: 'How it connects', status: 'Status', search: 'Search', searchPlaceholder: 'Name or code' },
    loading: 'Loading connections…',
    loadFailed: 'Could not load the connections',
    empty: 'No connection matches',
    emptyHint: 'Change the filters or add a connection.',
    envNotConfigured: 'Not set up',
    ready: 'Ready',
    notReady: 'Not ready',
    changeWaiting: 'Change waiting',
    noEndpoint: 'No address set',
    credential: 'Credentials v{{version}} · {{source}}',
    noCredential: 'No credentials',
    certificateDays_one: 'Certificate: {{count}} day left',
    certificateDays_other: 'Certificate: {{count}} days left',
    noCertificate: 'No client certificate',
    version0: 'Set up outside the Control Center',
    version: 'Settings v{{version}}',
    iana: 'IANA {{id}} · {{name}}',
    tlds_one: '{{count}} TLD',
    tlds_other: '{{count}} TLDs',
    domains_one: '{{formatted}} domain held',
    domains_other: '{{formatted}} domains held',
    openChanges_one: '{{count}} change waiting',
    openChanges_other: '{{count}} changes waiting',
    open: 'Open',
  },
  create: {
    title: 'Add a connection',
    description: 'A new connection is not used until it is switched on and a TLD channel points to it.',
    kindLabel: 'Who is it?',
    protocolLabel: 'How does Tino connect?',
    upstreamRest: 'Partner registrars are connected through their web service.',
    channelPreview: 'TLDs routed here will use the channel:',
    code: 'Code',
    codeHint: 'Short, permanent identifier: 2–32 lower-case letters, digits or hyphens. It cannot be changed later.',
    codeInvalid: 'Use 2–32 lower-case letters, digits or hyphens, starting with a letter or digit.',
    name: 'Display name',
    adapter: 'Integration',
    adapterHint: 'VNNIC EPP Gateway adds VNNIC’s own rules to standard EPP.',
    noRestAdapters: 'No web-service integration is available in this version of the platform yet.',
    spec: 'Provider technical documentation (name / version)',
    specHint: 'For reference only, e.g. the document title and version the provider gave Tino.',
    nextSteps: 'Once added, the address form opens; then the credentials (and client certificate for EPP).',
    reasonLabel: 'Reason',
    confirmLabel: 'confirmation',
    missing: 'Missing: {{fields}}',
    confirm: 'I have checked the kind, code and details above; the code cannot be changed later.',
    submit: 'Add connection',
    created: 'The connection was added. It is not used until it is switched on and a TLD is routed to it.',
    openNew: 'Enter the address',
    presetLabel: 'Start from a known provider',
    presetHint: 'Fills in the form below; check every value before adding the connection.',
    preset: {
      VNNIC_EPP: { title: 'VNNIC EPP Gateway', body: 'Registry for .vn domains. Direct connection with a client certificate.' },
      WEBNIC: { title: 'WebNIC', body: 'Partner registrar for TLDs Tino cannot register directly. Signs in with a WebNIC username and secret.' },
    },
  },
  upstream: {
    title: 'Partner registrar',
    hint: 'This registrar holds the domains Tino registers through it; its IANA ID and name are sent to VNNIC as the sponsoring registrar in gTLD reports.',
    legalName: 'Legal name',
    tradeName: 'Registrar name (as accredited)',
    ianaId: 'IANA registrar ID',
    ianaInvalid: 'A positive whole number.',
  },
  connection: {
    loading: 'Loading the connection…',
    loadFailed: 'Could not load the connection',
    notFound: 'No connection with this code',
    envLoadFailed: 'Could not load the connection settings',
    sections: 'Connection sections',
    tab: {
      overview: 'Overview',
      environments: 'Address',
      credentials: 'Credentials',
      certificates: 'Certificates',
      settings: 'Connection settings',
      tests: 'Tests',
      history: 'History',
    },
  },
  role: {
    title: 'Registrar role',
    hint: 'Who carries the registrar (RAA) duties for domains held through this connection. Default: WebNIC is an upstream registrar, other connections are the registrar of record.',
    current: 'Role',
    duties: 'What it means',
    change: 'Change role',
    changeTitle: 'Change the role to {{role}}',
    changeTo: 'Change to {{role}}',
    changed: 'Role changed.',
    value: { REGISTRAR_OF_RECORD: 'Registrar of record (Tino)', UPSTREAM_REGISTRAR: 'Upstream registrar' },
    effect: {
      REGISTRAR_OF_RECORD: 'Tino e-mails the WHOIS accuracy verification (registration, transfer-in, registrant change) and suspends when not verified; WHOIS privacy uses Tino\'s publication profile.',
      UPSTREAM_REGISTRAR: 'The upstream registrar verifies the registrant and suspends; Tino sends no verification e-mail of its own, reads the state back into the domain page and offers "Resend verification e-mail"; WHOIS privacy is switched at the upstream registrar.',
    },
  },
  overview: {
    identity: 'Connection',
    edit: 'Edit details',
    name: 'Name',
    code: 'Code',
    kind: 'Kind',
    channel: 'Channel for TLDs routed here',
    protocol: 'How it connects',
    adapter: 'Integration',
    status: 'Status',
    spec: 'Provider documentation',
    noSpec: 'Not recorded',
    inProductionUse: 'In production use',
    inProductionUseHint: 'Because it is in production use, changes to its status, integration or partner details need a second person’s approval.',
    domainsHeld: 'Domains held',
    created: 'Created',
    updated: 'Last changed',
    sponsorTitle: 'Sponsoring registrar for VNNIC gTLD reports',
    sponsorDescription: 'VNNIC’s international domain reports name the accredited registrar that holds each domain.',
    sponsorSource: 'Where it comes from',
    sponsorUpstream: 'For a partner registrar these are its own details; edit them with “Edit details”.',
    sponsorMapping: 'For a direct registry connection this is Tino’s own accreditation, set on the VNNIC page.',
    toSponsors: 'Sponsoring registrars →',
    environments: 'Connection settings',
    environmentsHint: 'What the connection needs before TLDs can use it.',
    envMissing: 'Nothing is set up yet.',
    openEnv: 'Open settings',
    configureEnv: 'Set up',
    tlds: 'TLDs routed here',
    tldsHint: 'New registrations and transfers-in of these TLDs use this connection. “default” means no channel was chosen and the TLD uses its default connection.',
    toTlds: 'TLD channels',
    noTlds: 'No TLD.',
    capabilities: 'Registry rules in force',
    capabilitiesHint: 'Recorded by the platform team; read-only here.',
    capabilitiesVersion: 'Version {{version}}, in force since {{from}}',
    noCapabilities: 'Not recorded yet.',
  },
  exercise: {
    title: 'OT&E business test',
    description:
      'Runs every domain operation once against the registry’s test system (OT&E), from the OT&E server: a test domain is registered with made-up contact details, changed, renewed and so on, and you see what the registry answered at each step. Nothing is sent to the live registry.',
    run: 'Run OT&E business test',
    runAgain: 'Run again',
    running: 'Running…',
    starting: 'Starting…',
    oteOnly: 'This test runs only on the OT&E (test) server; this is the production server.',
    dialogTitle: 'OT&E business test on {{name}}',
    dialogDescription: 'Real commands go to the registry’s test system (OT&E). The run is recorded with your name.',
    banner: 'A new test domain and test contacts are created in OT&E. Choose the steps; a step that needs another one selects it too.',
    stepsLabel: 'Steps',
    stepShort: { CHECK: 'name check', CONTACTS: 'contacts', CREATE: 'registration' },
    needs: 'needs {{list}}',
    deleteHint: 'Off unless you tick it: the registry may refuse to delete.',
    limits: 'One run at a time; a new start at most every {{seconds}} seconds and {{perHour}} times an hour.',
    latest: 'Latest run',
    history: 'Earlier runs',
    none: 'No run yet.',
    show: 'Details',
    hide: 'Hide',
    by: 'by',
    domain: 'Test domain',
    progress: '{{done}} of {{total}} steps',
    status: { RUNNING: 'Running', PASSED: 'All steps passed', FAILED: 'Some steps did not pass', ABANDONED: 'Interrupted' },
    abandonedHint: 'The server running this test restarted before the end. Start a new run.',
    probe: 'Run from: {{service}} (the registry only accepts the registered server addresses).',
    step: {
      CHECK: 'Check that the test name is free',
      CONTACTS: 'Create the test contacts (registrant, administrative, technical, billing)',
      CREATE: 'Register the name for 1 year with 2 name servers',
      INFO: 'Read the name back from the registry',
      NAMESERVERS: 'Change name servers (add one, remove one)',
      LOCK: 'Lock and unlock transfers',
      AUTH_INFO: 'Change the transfer code',
      DNSSEC: 'Add and remove a DNSSEC key',
      RENEW: 'Renew for 1 year and check the new expiry',
      REGISTRANT_CHANGE: 'Change the registrant to a new contact',
      TRANSFER: 'Ask for a transfer with a wrong code (must be refused), then read the transfer state',
      POLL: 'Read and acknowledge one registry message',
      DELETE: 'Delete the test name',
    },
    stepStatus: { PASSED: 'Passed', FAILED: 'Did not pass', UNKNOWN: 'Unclear', SKIPPED: 'Skipped', PENDING: 'Waiting', ACTIVE: 'In progress' },
    note: {
      BLOCKED: 'Not run, because “{{step}}” did not pass.',
      NOT_SUPPORTED: 'The registry does not offer this operation.',
      NAME_NOT_AVAILABLE: 'The test name is not free.',
      REJECTED: 'The registry refused it (see the answer below).',
      OUTCOME_UNKNOWN: 'No clear answer: the registry may or may not have done it. Nothing was repeated.',
      NOT_SENT: 'Nothing reached the registry (connection, sign-in, or a check before sending).',
      UNEXPECTED_RESULT: 'The registry answered, but not as expected.',
      MISMATCH: 'The registry accepted it, but reading the name back does not show the change.',
      NO_MESSAGE: 'No message was waiting, so acknowledging could not be tried.',
      STEP_TIMEOUT: 'The step took too long; the run stopped waiting for it.',
    },
    commands: 'Commands sent',
    commandLine: '{{command}} · {{outcome}}',
    commandOutcome: { SUCCESS: 'accepted', REJECTED: 'refused', UNKNOWN: 'unclear', NOT_SENT: 'not sent' },
    resultCode: 'answer {{code}}',
    svTrid: 'registry reference',
    clTrid: 'our reference',
    fact: {
      domain: 'Name',
      available: 'Free',
      reason: 'Reason',
      registrant: 'Registrant',
      admin: 'Administrative contact',
      tech: 'Technical contact',
      billing: 'Billing contact',
      period_years: 'Years',
      nameservers: 'Name servers',
      created_at: 'Registered',
      expires_at: 'Expires',
      pending_review: 'Waiting for registry review',
      statuses: 'Statuses',
      contacts: 'Contacts',
      sponsored_by_us: 'Held by us',
      expected: 'Expected',
      found: 'Found',
      added: 'Added',
      removed: 'Removed',
      nameservers_after: 'Name servers afterwards',
      lock: 'Lock',
      locked_before: 'Locked at first',
      locked_after_1: 'Locked after the first change',
      locked_after_2: 'Locked after the second change',
      changed: 'Changed',
      key_tag: 'Key tag',
      algorithm: 'Algorithm',
      digest_type: 'Digest type',
      expires_before: 'Expiry before',
      expected_after: 'Expected expiry',
      expires_after: 'Expiry after',
      new_registrant: 'New registrant',
      registrant_after: 'Registrant afterwards',
      expected_request_code: 'Expected answer to the wrong code',
      request_code: 'Answer to the wrong code',
      query_status: 'Transfer state',
      query_code: 'Answer to the state query',
      messages: 'Messages waiting',
      message_id: 'Message',
      remaining: 'Messages left',
      queued_at: 'Queued',
      object: 'About',
      statuses_after: 'Statuses afterwards',
      found_after: 'Still found',
      ds_found_after_add: 'DS record read back after adding',
    },
  },
  envTab: {
    addressTitle: 'Address',
    edit: 'Change address',
    configure: 'Set up',
    test: 'Check connection',
    blocked: 'A change is already waiting for approval. Approve, apply or cancel it first.',
    notConfigured: 'Not set up on the {{env}}',
    notConfiguredHint: 'Set the address to start; nothing is used until you switch the connection on.',
    notConfiguredVnnic: 'VNNIC’s published addresses (OT&E and production) are offered when you set it up.',
    versionInForce: 'Version {{version}} in force',
    sameAsHost: 'Same as the server address',
    extraCa: 'Extra authorities configured',
    systemCa: 'Standard trusted authorities',
    pendingTitle: 'Version {{version}} is waiting for approval',
    pendingBody: 'The proposed values are shown below. They take effect only after approval and applying.',
    readiness: 'Ready to use?',
    applied: 'Services using this connection',
    appliedHint: 'Which version each running service has loaded.',
    tests: 'Recent checks',
    testsHint: 'Each check is recorded with who ran it.',
    history: 'Version history',
  },
  envDialog: {
    titleEndpoint: 'Address of {{name}}',
    titleSettings: 'Connection settings of {{name}}',
    description: 'The whole configuration is saved as a new version; the previous one stays in the history.',
    firstTime: 'The connection is set up for the first time.',
    enabled: 'Switch the connection on',
    enabledHint: 'When off, the platform does not use this connection.',
    presetInUse: 'in use',
    hostHint: 'Host name or IP address only, without “https://”.',
    tlsHint: 'Strongly recommended; required in production.',
    tlsRequired: 'Always on in production.',
    advancedTls: 'Advanced encryption settings',
    serverNameHint: 'Only if the server’s certificate carries a different name than the address.',
    caHint: 'Only if the registry uses its own certificate authority; paste the public certificates.',
    baseUrlHint: 'The address the provider gave Tino, starting with https://.',
    httpsRequired: 'Production needs an encrypted address (https://).',
    accountHint: 'Tino’s account or reseller number at the provider, if it has one. Not secret.',
    headerHint: 'The field name the provider expects, e.g. X-Api-Key.',
    credentialsElsewhere: 'Passwords, tokens and keys are set on the Credentials tab.',
    totalSessions_one: 'In total {{count}} session per running service.',
    totalSessions_other: 'In total {{count}} sessions per running service.',
    otherFieldsKept: 'The address and encryption settings stay as they are.',
    settingsKept: 'Time limits and session pools stay as they are.',
  },
  settingsTab: {
    title: 'Connection settings',
    description: 'Time limits, pausing after failures and how many sessions each kind of work may use.',
    edit: 'Change settings',
    configureFirst: 'Set the address first.',
    noLimit: 'No limit',
    failures_one: '{{count}} failure',
    failures_other: '{{count}} failures',
    pools: 'Session pools',
    poolsHint: 'Separate sessions keep background work from slowing down customer actions.',
    defaultPool: 'Not set: one shared session (5 commands per second).',
    class: 'Kind of work',
  },
  credentials: {
    title: 'Sign-in credentials',
    descriptionEpp: 'The login ID and password Tino uses to sign in to the registry. Passwords are never displayed. New versions replace the one in use; old versions stay in the history.',
    descriptionRest: 'What Tino uses to sign in to the provider’s web service. Passwords, tokens and keys are never displayed. New versions replace the one in use; old versions stay in the history.',
    add: 'Set new credentials',
    needsEnvironment: 'Set the address first.',
    restNotVerified: 'The platform cannot yet confirm that these credentials work: the check only tests that the service can be reached.',
    none: 'No credentials yet',
    clientId: 'Username / client ID',
    hidden: 'Hidden (needs the credential management permission)',
    secret: 'Password or key',
    revoke: 'Revoke',
    destroy: 'Destroy value',
    destroyWhat: 'Password of credentials version {{version}}',
  },
  credDialog: {
    title: 'New credentials for {{name}}',
    description: 'Saved as a new version. The value you type is encrypted and never shown again.',
    current: 'Version {{version}} is in use.',
    none: 'No credentials are in use yet.',
    eppClientId: 'Registrar login ID',
    eppClientIdHint: '3–16 characters, as issued by the registry.',
    eppClientIdInvalid: 'Must have 3–16 characters.',
    eppPassword: 'Password',
    eppPasswordHint: '6–16 characters. This changes what Tino signs in with; it does not change the password at the registry.',
    eppPasswordInvalid: 'The password must have 6–16 characters.',
    username: 'Username',
    password: 'Password',
    token: 'Access token',
    apiKey: 'Access key',
    oauthClientId: 'Client ID',
    clientSecret: 'Client secret',
    restHint: 'Use exactly what the provider issued to Tino.',
    keep: 'Keep the current password',
    rotateProduction: 'The version in use keeps working until the new one is approved and applied.',
    rotateTest: 'The new version replaces the one in use immediately.',
  },
  certificates: {
    title: 'Client certificates',
    description: 'The certificate and private key Tino presents when it connects to the registry.',
    add: 'Certificate + key (separate files)',
    none: 'No client certificate',
    noneHint: 'Upload the certificate issued for Tino if the registry requires one.',
    requiredProduction: 'Production connections need a client certificate.',
    daysLeft_one: '{{count}} day left',
    daysLeft_other: '{{count}} days left',
    expired: 'Expired',
    subject: 'Issued to',
    issuer: 'Issued by',
    validity: 'Valid',
    serial: 'Serial number',
    fingerprint: 'Fingerprint (SHA-256)',
    key: 'Private key',
    destroyWhat: 'Private key of certificate version {{version}}',
  },
  certDialog: {
    title: 'New client certificate for {{name}}',
    description: 'The platform reads the certificate, checks its dates and that the key belongs to it.',
    pem: 'Certificate (public)',
    pemHint: 'Paste the certificate text issued to Tino first, then any intermediate certificates.',
    pemInvalid: 'Paste the full certificate text, including the BEGIN and END lines.',
    key: 'Private key',
    keepKey: 'Keep the current key (renewed certificate)',
    keyHint: 'Choose the key file; it is encrypted when saved and never displayed.',
  },
  revoke: {
    title: {
      credential: 'Revoke credentials version {{version}}',
      certificate: 'Revoke certificate version {{version}}',
    },
    bannerNote: 'Revoking takes effect immediately, without approval.',
    description: 'Revoking takes effect immediately, in production too, without approval, because it is used in emergencies. It is recorded.',
    activeTitle: 'This version is in use',
    activeBody: {
      credential: 'The connection stops working as soon as it is revoked, until new credentials are in force.',
      certificate: 'The connection stops working as soon as it is revoked, until a new certificate is in force.',
    },
    inactiveTitle: 'This version is not in use',
    inactiveBody: 'It can no longer be used afterwards.',
    confirmActive: 'I understand the connection stops working now.',
    confirm: 'Revoke this version.',
    submit: 'Revoke',
  },
  destroy: {
    title: 'Destroy the stored value',
    body: 'The value kept encrypted by the platform is erased permanently. The version keeps its history but can never be used again.',
    confirm: 'Erase the value permanently.',
    submit: 'Destroy value',
  },
  editConnection: {
    title: 'Edit {{name}}',
    description: 'Code, kind and how it connects cannot be changed after creation.',
    inProductionTitle: 'In production use',
    inProduction: 'Changes to the status, integration or partner details are saved as a proposal and need a second person’s approval. Changing only the name or documentation reference takes effect immediately; changed together with the above, everything waits for approval.',
    statusHint: 'A retired connection can no longer be chosen for TLDs.',
    immutable: 'The code, kind and connection method are permanent.',
    willNeedApproval: 'This change needs a second person’s approval before it takes effect.',
    appliesNow: 'This change takes effect immediately.',
  },
  versions: {
    version: 'Version',
    versionN: 'Version {{version}}',
    version0: '0 (setup)',
    version0Long: 'Set up outside the Control Center (version 0)',
    status: 'Status',
    enabled: 'On/off',
    endpoint: 'Address',
    reason: 'Reason',
    createdBy: 'Proposed',
    activated: 'In force since',
    none: 'No versions yet.',
    noneProvider: 'Nothing has been saved here yet.',
  },
  history: {
    title: 'Change history',
    description: 'Every change, check, approval and cancellation of this connection, newest first.',
    none: 'No recorded changes.',
    withApproval: 'four-eyes',
    outcome: { SUCCESS: 'Done', FAILED: 'Failed', DENIED: 'Refused' },
    actor: { SYSTEM: 'System', EXTERNAL: 'External' },
    action: {
      integration_connection_create: 'Connection added',
      integration_connection_update: 'Connection details changed',
      integration_environment_propose: 'Connection settings change proposed',
      integration_environment_activate: 'Connection settings change in force',
      integration_credential_propose: 'New credentials proposed',
      integration_credential_activate: 'New credentials in force',
      integration_credential_revoke: 'Credentials revoked',
      integration_certificate_propose: 'New certificate proposed',
      integration_certificate_activate: 'New certificate in force',
      integration_certificate_revoke: 'Certificate revoked',
      integration_tld_channel_propose: 'TLD channel change proposed',
      integration_tld_channel_activate: 'TLD channel change in force',
      integration_provider_setting_propose: 'VNNIC settings change proposed',
      integration_provider_setting_activate: 'VNNIC settings change in force',
      integration_sponsoring_registrar_propose: 'Sponsoring registrar change proposed',
      integration_sponsoring_registrar_activate: 'Sponsoring registrar change in force',
      integration_secret_store: 'Value stored encrypted',
      integration_secret_destroy: 'Stored value destroyed',
      integration_change_apply: 'Approved change applied',
      integration_change_cancel: 'Change cancelled',
      integration_connection_test: 'Connection checked',
      integration_ote_exercise_start: 'Start the OT&E business test',
      integration_ote_exercise_finish: 'OT&E business test finished',
      approval_request: 'Approval requested',
      approval_decide: 'Approval decided',
      integration_balance_read: 'WebNIC balance checked',
      integration_certificate_import_pkcs12: 'Certificate file import refused',
    },
  },
  tlds: {
    guideTitle: 'What are the kinds of registration channel?',
    own: "The TLD's own choice",
    inherited: 'By default: {{name}}',
    domainsVia_one: '{{formatted}} domain through {{name}}',
    domainsVia_other: '{{formatted}} domains through {{name}}',
    domainsViaMore: '{{formatted}} through {{name}}',
    openTld: 'Open .{{zone}}',
    title: 'TLD channels',
    subtitle: 'Which connection handles new registrations and transfers-in of each TLD.',
    explainTitle: 'What a channel change does',
    explain:
      'Only new registrations and transfers-in use the new connection. Domains already registered stay with the connection that holds them (renewals and changes keep going there) until they are transferred. A TLD without a choice uses its default connection.',
    filter: {
      channel: 'Channel',
      connection: 'Connection',
      source: 'Chosen',
      class: 'Kind of TLD',
      search: 'TLD',
      searchPlaceholder: 'e.g. com, vn',
    },
    col: { tld: 'TLD', kind: 'Channel kind', connection: 'Connection for new registrations', source: 'How it was chosen', channel: 'Registration channel', domains: 'Domains held', actions: 'Actions' },
    loading: 'Loading TLDs…',
    loadFailed: 'Could not load the TLD channels',
    empty: 'No TLD matches these filters.',
    noAdapter: 'Automatic registration not available yet',
    notReady: 'Connection not ready',
    pending: 'Change waiting',
    change: 'Change channel',
    history: 'History',
    noDomains: 'None',
    selectAll: 'Select all TLDs on this page',
    select: 'Select .{{zone}}',
    batch_one: 'Change channel of {{count}} TLD',
    batch_other: 'Change channel of {{count}} TLDs',
  },
  channelDialog: {
    title: 'Channel of .{{zone}}',
    description: 'Choose the connection for new registrations and transfers-in of this TLD.',
    currentLabel: 'Now',
    current: 'In use now',
    newConnection: 'New connection',
    newChannel: 'New channel:',
    scopeTitle: 'Existing domains do not move',
    scope: 'Only new registrations and transfers-in use the new connection. Domains already registered stay with the connection that holds them.',
    scopeDomains_one: 'Only new registrations and transfers-in use the new connection. The {{formatted}} domain already registered stays with {{name}}.',
    scopeDomains_other: 'Only new registrations and transfers-in use the new connection. The {{formatted}} domains already registered stay with {{name}}.',
    checks: 'Before you continue',
    sponsorCheck: 'For gTLDs, VNNIC reports need the sponsoring registrar of this connection (VNNIC page).',
    pendingExists: 'A change of this channel is already waiting for approval. Approve, apply or cancel it first.',
    applied: 'New registrations and transfers-in of this TLD use the new connection from now on.',
  },
  batchDialog: {
    title_one: 'Change the channel of {{count}} TLD',
    title_other: 'Change the channel of {{count}} TLDs',
    description: 'All selected TLDs move to one connection, with a single approval on the production server.',
    tlds: 'Selected TLDs',
    skipHint: 'TLDs that already use this connection, have a change waiting or are retired are skipped.',
    included_one: '{{count}} TLD included.',
    included_other: '{{count}} TLDs included.',
    skipped_one: '{{count}} TLD skipped',
    skipped_other: '{{count}} TLDs skipped',
    skip: {
      SAME_CONNECTION: 'already uses this connection',
      PENDING_CHANGE_EXISTS: 'a change is already waiting',
      TLD_RETIRED: 'retired',
    },
  },
  tldHistory: {
    defaultOf: 'Default connection of .{{zone}}',
    title: 'Channel history of .{{zone}}',
    description: 'Every channel choice for this TLD, newest first.',
    inline: 'Channel history',
    default: 'Default connection',
    connection: 'Connection',
    none: 'No channel was ever chosen',
    noneHint: 'The TLD has always used its default connection.',
  },
  vnnic: {
    title: 'VNNIC integrations',
    subtitle: 'Registrar API, eKYC, dossier service (HSTM) and gTLD reporting of this server.',
    catalogFailed: 'Could not load the list of VNNIC services',
    unknownProvider: 'This service is not available in this version.',
    loadFailed: 'Could not load these settings',
    usedBy: 'Used by:',
    fromEnvTitle: 'Still using the server settings',
    fromEnv: 'Nothing is saved here yet, so the services use the values set on the server (environment variables), if any. Once you save settings here, they replace the server values.',
    someOnEnv: 'Some services still use the server settings; they switch to the settings saved here within about {{seconds}} seconds.',
    restartTitle: 'Restart needed',
    restart: 'Switching this service on or off takes effect only after the services restart. Until then they keep working as before.',
    settingsTitle: 'Settings',
    edit: 'Change settings',
    configure: 'Set up here',
    test: 'Check reachability',
    preset: 'VNNIC’s published address for the {{env}}:',
    noPreset: 'VNNIC has not published an address for the {{env}}; enter the one VNNIC gave Tino.',
    matchesPreset: 'Matches VNNIC’s published address',
    differsFromPreset: 'Differs from VNNIC’s published address',
    notConfigured: 'Not set up on the {{env}}',
    notConfiguredHint: 'Set it up here to manage it from the Control Center.',
    applied: 'Services',
    appliedHint: 'Which settings each running service uses.',
    testsHint: 'Only checks that the service can be reached; no credentials or personal data are sent.',
    testsHintLogin: 'The reachability check sends no credentials; the sign-in check uses the stored client id and secret for one read. No personal data is sent.',
    secrets: 'Passwords and keys',
  },
  providerDialog: {
    title: 'Change settings: {{name}}',
    description: 'Saved as a new version; the previous one stays in the history. Passwords and keys are never shown again.',
    firstTime: 'Until now the services used the server settings. Saving here replaces them.',
    enabled: 'Switch this service on',
    advanced: 'Advanced settings',
    pathHint: 'Default: {{value}}',
    keep: 'Keep the current value',
    secretRequired: 'Needed when the service is switched on; it can stay empty while the service is off.',
    secretPermission: 'Changing a password or key needs the credential management permission; on the production server the approver needs it too.',
  },
  sponsor: {
    title: 'Sponsoring registrars (gTLD reports)',
    description: 'The accredited registrar named for each connection in VNNIC’s international domain reports.',
    connection: 'Connection',
    sourceLabel: 'From',
    source: { UPSTREAM_REGISTRAR: 'Partner registrar details', MAPPING: 'Tino’s accreditation', MISSING: 'Missing' },
    iana: 'IANA ID',
    name: 'Registrar name',
    zones: 'gTLDs (production)',
    domains: 'gTLD domains',
    set: 'Set',
    edit: 'Change',
    fromUpstream: 'Change on the connection page',
    missing: 'Some connections have no sponsoring registrar: VNNIC reports skip their gTLD domains until one is set.',
    none: 'No connection handles gTLDs.',
    dialogTitle: 'Sponsoring registrar of {{name}}',
    dialogDescription: 'The IANA ID and registrar name sent to VNNIC for domains held through this connection.',
    alwaysApproval: 'Always needs a second person',
    alwaysApprovalBody: 'This affects official reports, so every change is approved by a second person before it is applied.',
    confirm: 'I have checked the IANA ID and name against the accreditation.',
  },
  webnic: {
    allowListTitle: 'WebNIC only accepts calls from approved server addresses',
    allowList: 'WebNIC answers only requests that come from IP addresses on the account’s allow-list. Calls from any other address are refused as if the username or secret were wrong.',
    allowListHow: 'In the WebNIC partner portal, add the public (outgoing) IP address of each Tino server that talks to WebNIC to the IP allow-list: the OT&E server to the test account, the production server to the production account. Ask the platform team for these addresses.',
    authHint: 'Tino signs in with the WebNIC username and secret set on the Credentials tab and renews its access automatically.',
    authMethod: 'WebNIC username and secret',
    username: 'WebNIC API username',
    secret: 'WebNIC API secret',
    credentialHint: 'The username and secret from the WebNIC partner portal; the OT&E server uses the test account, the production server the production account.',
    keepSecret: 'Keep the current secret',
    overviewTitle: 'WebNIC',
    overview: 'This page shows the WebNIC account balance and checks the sign-in. Registrations of domain extensions whose channel is WebNIC are sent to WebNIC automatically.',
    createTitle: 'What WebNIC needs',
    createAddresses: 'Addresses (offered when you set up the connection):',
    createAuth: 'Sign-in: the WebNIC username and secret, set on the Credentials tab.',
    createIana: 'Enter WebNIC’s IANA registrar ID from the IANA registrar list; it is sent to VNNIC in gTLD reports.',
  },
  balance: {
    title: 'Account balance',
    description: "Tino's remaining balance at this provider. Reading it changes nothing at the provider.",
    loading: 'Asking the provider…',
    refresh: 'Refresh',
    refreshIn_one: 'Refresh possible in {{count}} second',
    refreshIn_other: 'Refresh possible in {{count}} seconds',
    empty: 'The provider reports no balance for this account.',
    fetchedAt: 'Checked at {{when}} ({{ago}}).',
    cached: 'The provider is asked at most once a minute; this is the latest answer.',
    unavailable: {
      notConfigured: 'Set the address and the WebNIC username and secret first.',
    },
    error: {
      notConfiguredServer: "The {{provider}} balance and name list come from VNNIC's Registrar API, configured for the server (address, client id, secret), which is not set up or not enabled.",
      openProviderSettings: 'Open the VNNIC Registrar API settings',
      authFailedVnnic: "Check the VNNIC Registrar API client id and secret in the provider settings, and that this server's IP address is registered with VNNIC.",
      failed: 'The balance could not be read',
      AUTH_FAILED: '{{provider}} refused the credentials',
      UNAVAILABLE: '{{provider}} could not be reached',
      NOT_CONFIGURED: 'The {{provider}} settings cannot be used yet',
      NOT_SUPPORTED: "This connection's provider offers no such function",
      FORBIDDEN: 'You do not have permission to see integration settings',
      NOT_FOUND: 'This connection no longer exists',
      authFailedBody: "Check the {{provider}} username and secret on the Credentials tab. If they are right, this server's address is most likely not on {{provider}}'s IP allow-list.",
      unavailableBody: "{{provider}} did not answer in time or answered unexpectedly. Nothing was changed; try again in a few minutes.",
      notConfiguredBody: 'Check the {{provider}} address, username and secret.',
      when: '{{provider}} was asked at {{when}}.',
    },
  },
  places: {
    title: "VNNIC's place list",
    description: "Provinces and wards of the two-level list VNNIC checks on contacts. Every subject address is picked and checked against it. The server syncs it from VNNIC daily.",
    counts: '{{provinces}} provinces, {{wards}} wards in force.',
    fromVnnic: 'Synced from VNNIC',
    fromSeed: 'National list (not synced from VNNIC yet)',
    seedHint: "The bundled national list is in use. Sync from VNNIC so that names and ids match VNNIC's exactly (the Registrar API must sign in).",
    syncNow: 'Sync from VNNIC now',
    syncCounts: '{{provinces}} provinces, {{wards}} wards, {{retired}} retired',
    automatic: 'automatic',
    outcome: { RUNNING: 'Running', SUCCEEDED: 'Succeeded', FAILED: 'Failed' },
    error: {
      AUTH_FAILED: 'VNNIC refused the client id / secret',
      UNAVAILABLE: 'VNNIC could not be reached',
      UNREADABLE_ANSWER: 'VNNIC answered in an unexpected form',
      IMPLAUSIBLE_LIST: "VNNIC's list was implausibly short and was not applied",
      DATABASE: 'Database error',
    },
  },
  accountBlock: { title: 'Account at the provider', openConnection: 'Open the connection' },
  accountDomains: {
    title: 'Names held at the provider',
    description: "The names the provider lists for Tino, compared with this connection's names in the platform.",
    loading: 'Loading the names…',
    fetchedAt: 'Fetched {{when}}.',
    keptNote: 'The server keeps this list for 10 minutes, so pages, filters and searches do not ask the provider again. Refresh to fetch it again.',
    summary: { atProvider: 'At the provider', inBoth: 'In the platform', onlyProvider: 'Not in the platform', onlyPlatform: 'Only in the platform' },
    onlyProviderHint: '{{count}} names the provider lists for Tino are not in the platform (usually names still managed by the previous system). They need to be brought into the platform to be renewed and managed.',
    filter: { all: 'All', only_provider: 'Not in the platform', in_both: 'In the platform', only_platform: 'Only in the platform' },
    search: 'Search names',
    empty: 'No names match.',
    notInPlatform: 'Not in the platform',
    notAtProvider: 'Not listed by the provider',
    col: { name: 'Name', type: 'Type', created: 'Registered', expires: 'Expires', platform: 'In the platform', actions: 'Actions' },
    page: 'Page {{page}}/{{pages}} · {{from}}–{{to}} of {{total}} names',
    select: 'Select {{name}}',
    selectPage: 'Select every name of this page that is not in the platform',
    selected: '{{count}} names selected',
    clearSelection: 'Clear selection',
    importOne: 'Bring into the platform',
    importSelected: 'Bring {{count}} names into the platform',
    viewRegistry: 'View at the registry',
    preview: {
      title: '{{name}} at the registry',
      description: 'Read at the registry now (domain:info and the registrant contact); nothing is changed.',
      loading: 'Reading at the registry…',
      readAt: 'Read {{when}}.',
      notFound: 'The registry does not hold this name.',
      roid: 'ROID',
      sponsor: 'Sponsoring registrar',
      sponsoredByUs: 'Tino ({{sponsor}})',
      sponsoredByOther: 'Another registrar ({{sponsor}})',
      created: 'Registered',
      expires: 'Expires',
      statuses: 'Registry statuses',
      nameservers: 'Name servers',
      registrant: 'Registrant contact handle',
      contacts: 'Other contacts',
      subject: 'Subject in the platform',
      openSubject: 'Open subject',
      subjectHostbill: 'A subject imported from HostBill eKYC, not linked to a customer yet: bringing the name in gives it to the chosen customer.',
      subjectManagedBy: 'A subject managed by customer {{customer}}: the name can only go to that customer.',
      importable: 'Can be brought in: the name is Tino\'s, not expired and in no pending state.',
      inPlatform: 'The name is already in the platform.',
      openDomain: 'Open domain',
      close: 'Close',
    },
    match: {
      MATCHED: "Exactly one subject holds the identity number the registry has for the registrant.",
      AMBIGUOUS: '{{count}} subjects hold the identity number the registry has: choose the subject.',
      NOT_MATCHED: "No subject holds the identity number the registry has: bringing it in makes a new subject from the registry's data (unverified), or choose one of the customer's subjects.",
      NO_IDENTITY: "The registry holds no identity number for the registrant: bringing it in makes a subject from the registry's data (unverified), or choose one of the customer's subjects.",
      UNSUPPORTED: "This registry does not tell a contact's identity: choose the subject.",
      UNAVAILABLE: 'The registrant contact could not be read ({{outcome}}): choose the subject or try again.',
    },
    import: {
      title: 'Bring {{count}} names into the platform',
      description: "For each name the platform reads it again at the registry, then creates its record from the registry's data (ROID, sponsor, registration and expiry dates, statuses, name servers) with the customer and the subject, and records its registrant, admin, tech and billing contacts with the handles the registry has. A subject no customer manages (imported from HostBill's eKYC) is given to this customer; a missing one is made from the registry's data. No command is sent to the registry; nothing is charged.",
      states: 'The name becomes Active; auto-renew is off and nothing is billed; its dossier is opened with the subject and waits for staff to complete it.',
      customer: 'Owning customer',
      registrant: 'Registrant subject',
      registrantAuto: 'Match by the identity number the registry has for the registrant',
      registrantChoose: "Choose one of the customer's subjects",
      bulkAutoOnly: "With several names the subject is matched for each name (none: made from the registry's data). A name whose identity number several subjects hold fails; bring it in alone and choose the subject.",
      noSubjects: 'The customer has no subjects.',
      chooseSubject: 'Choose a subject',
      reason: 'Reason',
      reasonHint: 'Kept in the audit log.',
      submit: 'Bring into the platform',
      running: 'Working {{done}}/{{total}}…',
      finished: 'Done: {{ok}} succeeded, {{failed}} failed.',
      retry: 'Retry {{count}} failed names',
      close: 'Close',
      waiting: 'Waiting',
      working: 'Working',
      openDomain: 'Open domain',
    },
    importAll: {
      button: 'Bring all into the platform',
      title: 'Bring every missing name into the platform',
      description:
        "The server brings in, one after another, every name the provider lists for Tino that the platform does not have, for the chosen customer, in the background by batches: the page need not stay open. Each name is read again at the registry, gets its subject (matched by identity number, or made from the registry's data) and its contacts with the handles the registry has. No command is sent to the registry, nothing is charged, the customer is not notified.",
      count: '{{count}} names not in the platform will be processed (the list read at {{when}}).',
      skipNote:
        'Names expired, pending delete, in redemption, pending transfer or not created yet; whose identity number several subjects hold; or whose subject belongs to another customer are skipped with the reason. Running it again is safe: names already in are skipped. Auto-renew is off.',
      customer: 'Customer receiving every name',
      submit: 'Start',
      panelTitle: 'Bring all into the platform',
      state: {
        PENDING: 'Waiting to run',
        RUNNING: 'Running',
        WAITING: 'Waiting for the registry',
        RETRYING: 'Retrying',
        COMPLETED: 'Finished',
        FAILED: 'Stopped on an error',
        MANUAL_REVIEW: 'Stopped, needs staff',
        CANCELLED: 'Cancelled',
      },
      progress: '{{done}}/{{total}} names processed',
      imported: 'Imported',
      skipped: 'Skipped',
      failed: 'Failed',
      remaining: 'Remaining',
      meta: 'Customer {{customer}} · started {{when}}',
      finishedAt: 'Finished at {{when}}.',
      byReason: 'By reason',
      issues: 'Latest skipped or failed names ({{count}})',
      outcome: { SKIPPED: 'Skipped', FAILED: 'Failed' },
      rerunHint: 'Click Bring all into the platform again to retry failed or missing names: names already in are skipped.',
      failure: 'Stopped: {{message}}',
    },
  },
  pkcs12: {
    caInFile: 'The file carries {{count}} CA certificate(s) not yet used to verify the server',
    caInFileHint: "Registries often issue their root CA in the same file. Use it as the root CA so Tino trusts their server (compare the fingerprint if the registry publishes it).",
    noCaInFile: "The file carries no CA certificate. If a connection test says the server is not trusted, upload the registry's root CA in the Root CA section.",
    caAlreadyTrusted: 'The CA certificates in the file are already used as the root CA.',
    open: 'Upload .p12 / .pfx file',
    title: 'Upload certificate file for {{name}}',
    description: 'A .p12 or .pfx file holds the private key, the certificate issued to Tino and its chain. The platform opens it, checks the dates and that the key matches, and keeps the key encrypted.',
    replaces: 'The new certificate replaces version {{version}} once it is in force.',
    first: 'This will be the first client certificate of this connection.',
    file: 'Certificate file (.p12 / .pfx)',
    fileHint: 'At most 64 KB. The file is sent to the platform over the encrypted connection and not kept in your browser.',
    choose: 'Choose file…',
    chooseOther: 'Choose another file…',
    noFile: 'No file chosen',
    tooLarge: 'This file is larger than 64 KB; a client certificate file is normally a few KB.',
    passphrase: 'File password',
    passphraseHint: 'The password chosen when the file was exported. It is used once to open the file and never stored.',
    passphraseCleared: 'For safety the password field was cleared; type it again to retry.',
    productionNote: 'On the production server the certificate is saved as a proposal; it is used only after a second person approves it and someone applies it.',
    testNote: 'On this server the new certificate is used immediately.',
    submit: 'Upload and use',
    applied: 'The certificate is in force. Services pick it up within a minute.',
    summaryTitle: 'What the file contains',
    keyType: 'Key type',
    chainLength: 'Certificates stored',
    chainCount_one: '{{count}} certificate',
    chainCount_other: '{{count}} certificates (with its chain)',
    versionState: 'Result',
    stateApplied: 'Version {{version}} is in force',
    statePending: 'Version {{version}} is waiting for approval',
  },
  vnnicPreset: {
    title: 'VNNIC’s address for the {{env}}',
    tls: 'encrypted, with client certificate',
    inUseHint: 'The connection uses VNNIC’s address. Upload the certificate file VNNIC issued for it below.',
    differentHint: 'The connection uses a different address. Switch to VNNIC’s address before uploading VNNIC’s certificate.',
    missingHint: 'Set up the connection with VNNIC’s address first; then upload the certificate file VNNIC issued.',
    setUp: 'Use VNNIC’s address',
  },
  certFiles: {
    threeFiles: "Registries usually issue three separate files: the certificate (.crt), the private key (.key, possibly password-protected) and their root CA. The certificate and key go here; the root CA goes in the Root CA section of the Certificates tab.",
    loadCertificate: 'Choose certificate file (.crt / .cer / .pem)',
    loadKey: 'Choose private key file (.key / .pem)',
    keyPassword: 'Private key password',
    keyPasswordHint: 'This key is password-protected. It is opened once when saved and stored encrypted; the password is not kept.',
    unreadable: 'The file could not be read (64 KB at most).',
  },
  rootCa: {
    fromChainTitle: 'The active certificate carries {{count}} CA certificate(s)',
    fromChainBody: "A registry's .p12 file usually includes its root CA. These CAs can verify the server without uploading a separate file.",
    fromChain: 'Use the CA from the uploaded certificate',
    title: "Registry root CA",
    description: "The CA certificate used to verify the registry's server (e.g. VNNIC). Needed when the server uses a private CA that is not among the public CAs.",
    none: 'No root CA configured',
    noneHint: "Only public CAs are trusted. If a connection test says the server's certificate is not trusted, upload the root CA the registry issued.",
    add: 'Upload root CA',
    replace: 'Replace root CA',
    root: 'Root CA',
    intermediate: 'Intermediate CA',
    dialogTitle: 'Root CA of {{name}}',
    dialogDescription: 'Saved as a new version of the connection settings; the previous version stays in the history.',
    explain: 'Paste or choose the CA file (.crt / .cer / .pem, several certificates allowed). Leave empty to trust public CAs only.',
    pem: 'CA certificate',
    pemHint: 'A public certificate only, not a private key.',
    emptyHint: 'Empty: only public CAs are trusted.',
    loadFile: 'Choose root CA file',
    needsEnvironment: 'Enter the address first.',
  },
  email: {
    crumb: 'Integrations',
    pageTitle: 'E-mail & templates',
    pageSubtitle: 'How notices to registrants and customers are sent, and what they say. A customer whose tier allows it can send under its own brand from its page.',
    platformTitle: 'Sending settings (platform)',
    platformDescription: 'Used for every customer, except customers whose tier allows their own settings and who have saved them.',
    customerTitle: 'Sending settings of this customer',
    customerDescription: 'E-mails about this customer\'s domains and services are sent from its own server and address, under its brand.',
    sendingWith: {
      OWN: 'Sends with these settings',
      PLATFORM: 'Sends with the platform settings',
      NONE: 'Nothing is sent yet',
    },
    lastSaved: 'Last saved {{when}} by {{who}}',
    usesPlatform: 'This customer has no settings of its own: its e-mails go out with the platform settings, using its own templates and brand if it has them.',
    noneYet: 'No settings yet: e-mails wait until settings are saved here or for the platform.',
    platformMissing: 'No sending settings yet: notices are queued but not sent until you save them.',
    enabled: 'Send e-mails',
    enabledHint: 'Off: for the platform, notices are not sent; for a customer, the platform settings are used.',
    server: 'Mail server (SMTP)',
    host: 'Server',
    port: 'Port',
    security: 'Encryption',
    securities: {
      STARTTLS: 'STARTTLS (port 587)',
      TLS: 'TLS (port 465)',
      NONE: 'None (local relay only)',
    },
    username: 'User name',
    password: 'Password',
    passwordSaved: 'A password is saved (never shown).',
    passwordNone: 'No password saved.',
    passwordKeep: 'Keep the saved password',
    passwordReplace: 'Replace it',
    passwordSet: 'Set a password',
    passwordClear: 'Remove it',
    newPassword: 'New password',
    sender: 'Sender',
    fromName: 'Sender name',
    fromAddress: 'Sender address',
    replyTo: 'Reply-to address',
    bcc: 'Copy of every message to',
    bccHint: 'Optional, e.g. an archive mailbox.',
    brand: 'Brand shown in e-mails',
    brandFields: {
      name: 'Brand name',
      website: 'Website',
      support_email: 'Support e-mail',
      support_phone: 'Support phone',
      logo_url: 'Logo URL (https)',
      primary_color: 'Main colour',
      address: 'Address',
      footer: 'Footer text',
      action_base_url: 'Address for confirmation links',
    },
    actionBaseHint: 'Optional: where links in e-mails point (must serve /confirm/…). Empty: the platform address.',
    save: 'Save settings',
    saved: 'Settings saved.',
    testTo: 'Send a test message to',
    sendTest: 'Send test',
    testSent: 'Test message accepted by the server: {{answer}}',
    tpl: {
      title: 'Templates',
      descriptionPlatform: 'Built-in templates exist for every notice in Vietnamese and English; change any of them for your brand.',
      descriptionCustomer: 'This customer\'s own templates; any it does not change come from the platform (or the built-in ones).',
      notice: 'Notice',
      edit: 'Edit',
      sources: {
        CUSTOMER: 'This customer',
        PLATFORM: 'Platform',
        BUILT_IN: 'Built-in',
      },
      sourceNote: {
        CUSTOMER: 'This customer\'s own version.',
        PLATFORM: 'The platform\'s version: saving creates an own copy.',
        BUILT_IN: 'The built-in version: saving creates an own copy.',
      },
      subject: 'Subject',
      html: 'HTML body',
      htmlHint: 'Put inside the layout. Variables are escaped automatically.',
      layoutHint: 'Must contain {{ content }} where each notice\'s body goes.',
      text: 'Plain-text body',
      variables: 'Variables: {{ domain }}, {{ recipient_name }}, {{ brand.name }}, {{ brand.support_email }}… and those of each notice (see the built-in template). Conditions: {% if x %}…{% endif %}.',
      refreshPreview: 'Update preview',
      preview: 'Preview (sample data)',
      save: 'Save template',
      saved: 'Template saved.',
      revert: 'Back to default',
      close: 'Close',
    },
    log: {
      title: 'Sent e-mails',
      subtitle: 'Every notice sent or waiting to be sent, what was sent and why a message failed.',
      status: 'Status',
      statuses: {
        PENDING: 'Waiting',
        SENDING: 'Sending',
        SENT: 'Sent',
        FAILED: 'Failed',
        SUPPRESSED: 'Not sent (off)',
        HELD: 'Waiting for Zalo',
        REPLACED: 'Sent by Zalo instead (no e-mail)',
      },
      any: 'Any',
      recipient: 'Recipient',
      search: 'Search',
      notice: 'Notice',
      to: 'To',
      from: 'From',
      attempts: 'Attempts',
      created: 'Created',
      sent: 'Sent',
      template: 'Template',
      empty: 'No e-mails',
      first: 'Newest',
      older: 'Older',
      detailTitle: 'E-mail',
      detailDescription: 'What was sent (the one-time link is not kept).',
      linkNote: 'This message contained a one-time link; it is not kept in the log.',
      whatWasSent: 'What was sent',
      resend: 'Send again',
      requeued: 'Queued again.',
    },
    keys: {
      'layout': 'Layout (header and footer of every e-mail)',
      'domain.expiry_reminder': 'Expiry reminder',
      'domain.expired': 'Domain expired',
      'domain.renewed': 'Renewal done',
      'domain.deleted': 'Domain deleted',
      'transfer.out_requested': 'Transfer out requested',
      'transfer.out_completed': 'Transferred out',
      'transfer.in_completed': 'Transfer in completed',
      'transfer.code_issued': 'New transfer code issued',
      'registrant_change.consent': 'Registrant change: consent request',
      'registrant_change.completed': 'Registrant change done',
      'registrant_change.rejected': 'Registrant change refused',
      'contact.verify_email': 'Registrant e-mail verification',
      'contact.unverified_suspended': 'Suspended: registrant not verified',
      'contact.data_reminder': 'Yearly data reminder',
      'abuse.report_received': 'Abuse report received',
      'declaration.correction_required': 'Declaration to be supplemented',
      'system.test': 'Test message',
      'dossier.correction_required': 'Dossier needs a correction',
      'domain.registered': 'Registration done',
      'dossier.reminder': 'Dossier still missing (reminder)',
      'billing.payment_received': 'Payment received',
      'billing.invoice_issued': 'Invoice issued',
      'billing.fee_notice': 'Fee notice: services due',
      'service.overdue': 'Service payment overdue',
      'service.suspended': 'Service suspended',
      'service.terminated': 'Service terminated',
    },
  },
  routing: {
    tiles: {
      connections: 'Registrars & connections',
      connectionsHint: '{{direct}} direct · {{upstream}} through a partner registrar',
      tlds: 'TLDs with a registration route',
      tldsHint: '{{own}} set here · {{inherited}} by default',
      inUse: 'Connections taking TLDs',
      inUseHint: 'of {{total}} connections in use',
      review: 'To review',
      reviewHint: '{{tlds}} TLDs on a connection that is not ready · {{changes}} changes waiting',
    },
    registrars: {
      title: 'Registrars & connections',
      description: 'Who is the registrar of the names that go through each connection',
      col: { connection: 'Connection', role: 'Role', tlds: 'TLDs', status: 'State' },
      ready: 'Ready',
      notReady: 'Not ready',
      noTlds: 'None',
      iana: 'IANA {{id}} · {{name}}',
    },
    matrix: { title: 'New-registration route per TLD', description: 'The connection that takes each TLD’s new registrations and transfers in' },
    explain: {
      title: 'Which route a name takes',
      description: 'Where a new registration would go. Nothing is sent.',
      name: 'Domain name',
      placeholder: 'e.g. example.com.vn',
      run: 'Show route',
      invalid: 'Enter a domain name, e.g. example.vn',
      noTld: 'We do not sell the extension of {{name}}.',
      tld: 'TLD',
      connection: 'Connection',
      registry: 'Registry',
      registryUnknown: 'Not known',
      own: 'the TLD’s own route',
      inherited: 'by default',
      notReady: 'The connection is not ready: new registrations cannot be sent yet.',
      pending: 'A change to {{name}} is waiting for approval.',
      suspended: 'This TLD is not on sale.',
      openTld: 'Open .{{zone}}',
    },
    noFailover: {
      title: 'Existing names never move to another connection by themselves',
      body: 'Renewals, updates and deletions always go through the connection that holds the name. A new route applies to new registrations and transfers in only; moving a name to another registrar is a transfer of its own.',
    },
  },
  support: {
    group: { YES: 'Takes .{{zone}}', UNKNOWN: 'Not known', NO: 'Does not take .{{zone}}' },
    groupBatch: { YES: 'Takes every selected TLD', UNKNOWN: 'Not known for some', NO: 'Does not take some' },
    basis: {
      CURRENT_CHANNEL: 'The TLD’s current route',
      ZONE_DEFAULT: 'The TLD’s default connection',
      HOLDS_DOMAINS: 'Holds .{{zone}} names',
      IN_PUBLISHED_LIST: 'On the list of extensions {{name}} offers',
      NOT_IN_PUBLISHED_LIST: '.{{zone}} is not on the list of extensions {{name}} offers',
      LIST_UNAVAILABLE: 'The list of extensions of {{name}} cannot be read now',
      NO_PUBLISHED_LIST: '{{name}} does not publish a list of extensions',
      VNNIC_ZONE: 'VNNIC takes the .vn extensions',
      VNNIC_ONLY_VN: 'VNNIC only takes .vn extensions',
      OPERATOR_UNKNOWN: 'The registry of .{{zone}} is not known',
      NOT_DECLARED: 'Takes no TLD yet, so its registry is not known',
      SAME_OPERATOR: 'Same registry as the TLDs it already takes',
      OTHER_OPERATOR: 'Connection of another registry ({{operators}})',
    },
    unknownHint: 'You can pick it; after the change, use “Try a name” on the TLD page.',
    loading: 'Checking which connections take this TLD…',
    failed: 'Could not check which connections take this TLD; every connection is shown.',
    batchMissing: 'Does not take: {{list}}',
    batchUnknown: 'Not known for: {{list}}',
    showHidden: 'Show {{count}} connections that do not take it',
    hideHidden: 'Hide connections that do not take it',
  },
  health: {
    summary: '{{ready}} ready · {{attention}} need attention',
    summaryHint: 'Each connection has its own queue, rate limits and breaker',
    state: { READY: 'Ready', NOT_READY: 'Not ready', TEST_FAILED: 'Last check failed', SUSPENDED: 'Suspended', RETIRED: 'Retired', NOT_CONFIGURED: 'Not set up' },
    commands: 'Commands, {{hours}} h',
    slowest: 'Slowest',
    unresolved: 'Outcome unknown',
    balance: 'Balance at the provider',
    balanceError: 'Could not ask',
    certificate: 'Client certificate',
    certificateDays: '{{count}} days left',
    noCertificate: 'None',
    lastTest: 'Last check',
    noZones: 'Takes no TLD yet',
    moreZones: '+{{count}}',
    detail: 'Details',
    recent: {
      title: 'Recent commands',
      description: 'Commands sent to registries and partner registrars, without personal data',
      open: 'Open the command log',
      empty: 'No command yet.',
      hidden: 'Viewing commands needs the registry permission.',
      col: { at: 'Time', connection: 'Connection', command: 'Command', object: 'Name', result: 'Result' },
    },
    attention: {
      title: 'Needs attention',
      description: 'A connection check does not replace the outcome of each command',
      certificates: 'Client certificates expiring',
      certificatesHint: 'Under 30 days: {{list}}',
      notReady: 'Connections not ready',
      notReadyHint: '{{list}}',
      unresolved: 'Commands with an unknown outcome',
      unresolvedHint: 'Waiting for reconciliation',
      drift: 'Differences with the registry',
      driftHint: 'Open reconciliation items',
      messages: 'Registry messages not handled',
      messagesHint: 'Messages the registry sent',
      changes: 'Changes waiting for approval',
      changesHint: 'On the connections above',
      none: 'Nothing needs attention.',
    },
    breaker: {
      title: 'Each connection stops by itself after repeated failures',
      body: 'After several failures in a row a connection pauses sending for a while, then tries again; other connections are not affected. New registrations follow the TLD’s route; existing names go through the connection that holds them.',
    },
    explainTitle: 'Connections and registration routes',
  },
  connectionTiles: {
    tlds: 'TLDs taken',
    tldsHint: '{{own}} set here · {{inherited}} by default',
    domains: 'Names held',
    changes: 'Changes waiting',
    lastTest: 'Last check',
    certificate: 'Client certificate',
  },
} as const;
